/**
 * The session boundary.
 *
 * A kiosk is a wall-mounted panel in a public room, and until this existed the
 * only timer that ever returned it to the attract screen was a flat twelve
 * seconds on the success screen — *after* the money was taken. Everywhere else
 * an abandoned order sat indefinitely. Someone adds three drinks, is called
 * away, and the next customer arrives at a checkout screen holding a stranger's
 * basket with a payment button under it. That is the failure this avoids, and
 * it is a money failure rather than a tidiness one.
 *
 * **It warns before it acts.** A silent expiry is its own defect: a customer
 * reading the menu slowly, or someone helping a person who needs longer, has
 * the screen cleared out from under them with no way to say "still here". So
 * the countdown is visible and one touch anywhere cancels it. Published kiosk
 * guidance converges on the same shape — a 30-to-90-second idle period with a
 * countdown and a one-tap continue — and on never expiring silently.
 *
 * **What it must never interrupt.** A charge in flight. `paying` is true only
 * while a terminal is authorising or a QR is settling, and while it is true
 * this component does not run a clock at all. It is deliberately *not* the
 * update gate's `paymentInFlight`, which the payment screen holds for its whole
 * lifetime: a customer who reaches the payment screen and wanders off is still
 * an abandoned basket and still has to be cleared.
 *
 * `welcome` and `success` are exempt for opposite reasons — one is already the
 * attract screen, and the other owns a shorter timer of its own.
 */
import { useCallback, useEffect, useRef, useState } from 'react'
import { Button, Sheet } from '@/components/ui'
import { useT } from '@/i18n'
import { useKiosk } from '@/store/kiosk'
import { idleVerdict } from '@/services/idle'
import { abandonOrder } from '@/services/pay'

/** The clock only has to be as fine as the number on the screen. */
const TICK_MS = 250

/**
 * Activity, as broadly as the panel can report it.
 *
 * Captured at the window, because most of these fire on elements deep in a
 * scroller and `scroll` does not bubble at all. `pointermove` is in the list
 * for the mouse a developer uses and costs nothing on glass, where it only
 * fires while a finger is already down.
 */
const ACTIVITY = ['pointerdown', 'pointermove', 'keydown', 'wheel', 'touchstart', 'scroll'] as const

export function IdleGuard() {
  const t = useT()
  const step = useKiosk((s) => s.step)
  const paying = useKiosk((s) => s.paying)
  const reset = useKiosk((s) => s.reset)

  /**
   * Give 1C its stock back before the cart is cleared.
   *
   * Read from `getState()` rather than through a selector: this runs inside a
   * timer callback and from the warning's own button, and a subscribed value
   * would put `pendingOrderId` in the effect's dependency list — re-arming the
   * idle clock every time a customer taps a payment method.
   */
  const abandonPending = () => {
    const { pendingOrderId, setPendingOrderId } = useKiosk.getState()
    if (pendingOrderId === null) return
    abandonOrder(pendingOrderId)
    setPendingOrderId(null)
  }

  /** Null when nothing is being counted; otherwise milliseconds still to run. */
  const [remaining, setRemaining] = useState<number | null>(null)
  const lastActivity = useRef(Date.now())
  /*
   * Whether the countdown is on screen, readable from inside the listener.
   *
   * Once the warning is up, ordinary activity stops counting as "still here".
   * It has to: the sheet covers the panel, so the very `pointerdown` that is on
   * its way to the "start over" button would otherwise be read as activity,
   * dismiss the sheet, and unmount the button before the click landed. While
   * the warning is showing, the only answers are its own two controls and the
   * scrim — all of which say what they mean.
   */
  const warning = useRef(false)
  warning.current = remaining !== null

  const stillHere = useCallback(() => {
    lastActivity.current = Date.now()
    setRemaining(null)
  }, [])

  // One clock, driven off a timestamp, rather than a timer torn down and rebuilt
  // on every touch. A customer scrolling a menu fires hundreds of these.
  useEffect(() => {
    lastActivity.current = Date.now()
    const listener = () => {
      if (warning.current) return
      lastActivity.current = Date.now()
    }
    for (const event of ACTIVITY) window.addEventListener(event, listener, { capture: true, passive: true })

    const tick = setInterval(() => {
      // The policy is `services/idle.ts` and it is tested there. This component
      // owns only the clock, the listeners and the sheet.
      const verdict = idleVerdict({ step, paying, quietForMs: Date.now() - lastActivity.current })
      if (verdict.kind === 'expire') {
        /*
         * Hand back any reservation before the cart goes.
         *
         * This is the commoner abandonment by far — a customer who taps a
         * payment method and simply walks off — and it is the one the payment
         * screen cannot do itself, because `reset()` is what unmounts it.
         */
        abandonPending()
        // Order matters: stop the clock before resetting, so the step change
        // this causes cannot race the next tick.
        setRemaining(null)
        reset()
        return
      }
      setRemaining(verdict.kind === 'warn' ? verdict.remainingMs : null)
    }, TICK_MS)

    return () => {
      clearInterval(tick)
      for (const event of ACTIVITY) window.removeEventListener(event, listener, { capture: true })
    }
  }, [step, paying, reset])

  if (remaining === null) return null
  const seconds = Math.ceil(remaining / 1000)

  return (
    <Sheet open onClose={stillHere} labelledBy="idle-title">
      <div className="flex flex-col items-center gap-8 px-12 py-14 text-center">
        <h2 id="idle-title" className="display text-[length:var(--text-kiosk-xl)] uppercase">
          {t('idle.title')}
        </h2>

        {/*
          The number is the biggest thing on the sheet and it is hidden from
          assistive technology on purpose: a live region ticking once a second
          talks over the two buttons it is asking the customer to press. The
          sentence below announces once, when the sheet appears, which is the
          part that carries the instruction.
        */}
        <p aria-hidden="true" className="display text-[length:var(--text-kiosk-3xl)] tabular-nums">
          {seconds}
        </p>
        <p aria-hidden="true" className="eyebrow">
          {t('idle.seconds')}
        </p>

        <p role="alert" className="text-[length:var(--text-kiosk-sm)] text-[var(--color-ink-soft)]">
          {t('idle.body')}
        </p>

        <div className="flex w-full flex-col gap-4">
          <Button size="large" className="w-full uppercase" onClick={stillHere}>
            {t('idle.continue')}
          </Button>
          <Button
            variant="text"
            className="w-full text-[var(--color-ink-soft)] uppercase"
            onClick={() => {
              setRemaining(null)
              abandonPending()
              reset()
            }}
          >
            {t('idle.finish')}
          </Button>
        </div>
      </div>
    </Sheet>
  )
}
